import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatUnderlined as UnderlineIcon,
  Subscript as SubscriptIcon,
  Superscript as SuperscriptIcon,
} from '@mui/icons-material';
import { Box, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import React from 'react';
import type { TableCellStyle } from '../../template/Table';
import { execInlineFormat, type InlineFormatCommand } from './tableCellEvents';
import { emptyCell, resolveCellStyle } from './tableModel';
import type { useTableEditor } from './useTableEditor';

type Editor = ReturnType<typeof useTableEditor>;

const ROW_STYLES: { value: TableCellStyle; label: string }[] = [
  { value: 'header', label: 'Header' },
  { value: 'subHeader', label: 'Sub' },
  { value: 'normal', label: 'Normal' },
];

const INLINE_FORMAT_BUTTONS: {
  command: InlineFormatCommand;
  label: string;
  icon: React.ReactNode;
}[] = [
  { command: 'bold', label: 'Bold', icon: <BoldIcon fontSize="small" /> },
  { command: 'italic', label: 'Italic', icon: <ItalicIcon fontSize="small" /> },
  { command: 'underline', label: 'Underline', icon: <UnderlineIcon fontSize="small" /> },
  { command: 'subscript', label: 'Subscript', icon: <SubscriptIcon fontSize="small" /> },
  { command: 'superscript', label: 'Superscript', icon: <SuperscriptIcon fontSize="small" /> },
];

/**
 * Row-style + inline-format controls for the focused cell, shown in the table
 * toolbar. Inline bold/italic/underline/subscript/superscript is only offered
 * on a `normal` cell — a header / sub-header row is bold by its style.
 */
export default function TableStyleControls({ editor }: { editor: Editor }) {
  const focus = editor.focus;
  const [fr, fc] = focus ?? [-1, -1];
  const rowStyle: TableCellStyle = focus
    ? resolveCellStyle(editor.grid[fr]?.[fc] ?? emptyCell(), fr)
    : 'normal';
  const canFormatInline = Boolean(focus) && rowStyle === 'normal';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={focus ? rowStyle : null}
        onChange={(_event, value: TableCellStyle | null) => {
          if (focus && value) editor.setRowStyle(fr, value);
        }}
        aria-label="Row style"
      >
        {ROW_STYLES.map((option) => (
          <ToggleButton key={option.value} value={option.value} disabled={!focus}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Box sx={{ display: 'flex' }}>
        {INLINE_FORMAT_BUTTONS.map(({ command, label, icon }) => (
          <Tooltip key={command} title={`${label} (normal cells only)`}>
            <span>
              <ToggleButton
                size="small"
                value={command}
                disabled={!canFormatInline}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => execInlineFormat(command)}
                aria-label={label}
              >
                {icon}
              </ToggleButton>
            </span>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
}
